import GameObject from '../../../GameObject/GameObject';
import TransformComponent from '../../../Component/Transform';
import AnimationClip from '../AnimationClip';
import BaseCard from '../../../Prototype/Level/Card/BaseCard';
import { Easing, Tween } from '@tweenjs/tween.js';
import ZapCard from '../../../Prototype/Level/Card/ZapCard';
import Renderable from '../../../Component/Renderable';
import Container from '../../../Prototype/Container';
import { delay } from '../../../Utility/Task/TaskQueue';
import {
    GetAnimationClip,
    PlayAnimation,
    StartAnimation,
    AwaitIterators,
    StartAnimationClip,
} from '../AnimationHelper';
import Sprite from '../../../Prototype/Sprite';
import Point from '../../../Utility/Point';
import PIXIRenderer from '../../Render/PIXIRenderer';
import { DEG_TO_RAD } from 'pixi.js';
import CardSystem from '../../Card/CardSystem';
import { showCardScoreEffect } from '../CardAnimationHelper';
import { UIEvent } from '../../UI/BaseUISystem';
import { DragonbonesDefault } from './DragonbonesDefault';
import SpriteComponent from 'Component/Render/Sprite';
import Armature from 'Prototype/Armature';

interface ICardZapMetaData {
    system: CardSystem;
    sourceCard: ZapCard;
    targetCards: BaseCard[];
}

export class CardZap extends AnimationClip {
    public *execute(
        animator: GameObject,
        meta: ICardZapMetaData
    ): IterableIterator<number> {
        // Create containers for proper depth sorting.
        const cardsContainer = GameObject.Instantiate(Container);

        cardsContainer.addChild(meta.sourceCard);
        meta.targetCards.forEach(card => {
            cardsContainer.addChild(card);
        });

        animator.addChild(cardsContainer);

        const sourceCardTransform = meta.sourceCard.getComponent(
            TransformComponent
        );
        const sourceCardTween = this.getTween(sourceCardTransform);
        yield* this.startTween(
            sourceCardTween
                .to({ rotation: 0 }, 300)
                .easing(Easing.Quadratic.Out)
        );

        // Fade in the overlay.
        const overlay = animator.getChildByName('overlay');
        StartAnimation(overlay, 'OverlayFadeIn');

        const scaleTween = this.getTween(sourceCardTransform.scale);
        scaleTween.to({ x: 0.8, y: 0.8 }, 50).easing(Easing.Quadratic.InOut);

        yield* this.startTween(scaleTween);

        scaleTween.to({ x: 1, y: 1 }, 50);
        scaleTween.start(this.elapsedTime);

        yield* delay(200);

        const cardTransform = meta.sourceCard.getComponent(TransformComponent);
        const centerTween: Tween = this.getTween(cardTransform);
        centerTween
            .to(
                { x: PIXIRenderer.mapViewport.screenWidth / 2 },
                300
            )
            .easing(Easing.Linear.None).start(this.elapsedTime);

        const cardArmature = meta.sourceCard.getArmature();
        cardArmature.getComponent(Renderable).alpha = 1;
        meta.sourceCard
            .getChildByName('cardFrontSprite')
            .getComponent(SpriteComponent).visible = false;
        meta.sourceCard
            .getChildByName('cardBackSprite')
            .getComponent(SpriteComponent).visible = false;

        StartAnimationClip(
            new DragonbonesDefault(cardArmature, {
                playTimes: 1,
                animationName: 'activated'
            })
        );

        yield* delay(400);

        StartAnimationClip(
            new DragonbonesDefault(cardArmature, {
                playTimes: 1,
                animationName: 'cut'
            })
        )

        yield* delay(1100);

        const cardsLeft: BaseCard[] = [];
        const cardsRight: BaseCard[] = [];

        meta.targetCards.forEach(card => {
            const transform = card.getComponent(TransformComponent);
            if (transform.x > sourceCardTransform.x) {
                cardsRight.push(card);
            } else {
                cardsLeft.push(card);
            }
        });

        cardsRight.sort(
            (a, b) =>
                a.getComponent(TransformComponent).x -
                b.getComponent(TransformComponent).x
        );

        cardsLeft.sort(
            (a, b) =>
                a.getComponent(TransformComponent).x -
                b.getComponent(TransformComponent).x
        );

        const cards = cardsLeft.concat(cardsRight);

        const length = cards.length;

        const cutDuration = 530;

        showCardScoreEffect(meta.system.scene, meta.sourceCard);

        meta.system.scene.emit('UpdateScoreProgress');

        if (length > 0) {
            for (let i = 0; i < length; i++) {
                const armature = GameObject.Instantiate(Armature, {
                    name: 'card_armature',
                    dragonbonesName: 'scythe',
                    armatureName: 'card_removal_effect',
                    alpha: 1,
                    playOnStart: true,
                    playTimes: 1,
                });
                if (cards[i]) {
                    cards[i]
                        .getChildByName('cardFrontSprite')
                        .getComponent(SpriteComponent).visible = false;
                    cards[i]
                        .getChildByName('cardBackSprite')
                        .getComponent(SpriteComponent).visible = false;
                    cards[i].addChild(armature);
                    cards[i].hideAndDestroyModifiers();
                    yield* delay(cutDuration / length)
                    showCardScoreEffect(meta.system.scene, cards[i]);
                }
            }
        } else {
            yield* delay(cutDuration)
        }

        meta.sourceCard.getComponent(Renderable).visible = false;

        // Fade out.
        yield* PlayAnimation(GetAnimationClip('OverlayFadeOut', overlay));
        meta.system.scene.emit(UIEvent.RESTORE_INTERACTIVITY, true);
    }
}
